import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as TimerIcon } from '../../../assets/icons/clock-icon.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomOtpInput from '../../../shared-components/custom-otp-input/CustomOtpInput';
import { getLocalStorageItem, setLocalStorageItem } from '../../../shared-functions/LocalStorage';
import auth from '../../../utils/api/auth';

/**
 * Email verification component
 * @returns {any} The rendered Email verification component.
 * @description Email verification component is a component for email verification functionality.
 */
function EmailVerification() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const [OTP, setOTP] = useState<string[]>(Array(6).fill(''));
  const [resendTimer, setResendTimer] = useState<number>(30);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);
  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState('');

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  /**
   * Resolves which message to show under the OTP field: incomplete entry
   * takes priority, otherwise the API-reported invalid-OTP error.
   * @returns {string} translated error message
   */
  const resolveOtpErrorMessage = () => {
    if (formStatus.showFormErrors && OTP.some((digit) => digit === '')) {
      return t('pleaseEnterTheOtpReceivedThatWhichYouRegisteredEmailId');
    }
    return otpErrorMessage;
  };

  /**
   * Clears the invalid-OTP error state whenever the user edits the OTP digits.
   */
  useEffect(() => {
    setOtpError(false);
    setOtpErrorMessage('');
  }, [OTP]);

  /**
   * Function to verify enter OTP
   */
  const handleSubmitOtp = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (!OTP.some((digit) => digit === '')) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });
      const emailOTP = OTP.join('');
      auth
        .verifyEmail({ otp: emailOTP })
        .then(() => {
          // Update userVerification in local-storage;
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          let updatedUserVerification;
          const userVerification = getLocalStorageItem('userVerification');
          if (userVerification?.redirectToPhoneVerification) {
            updatedUserVerification = {
              ...userVerification,
              redirectToEmailVerification: false,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/phone-number-verification');
          } else if (!userVerification?.isInitialPasswordReset) {
            updatedUserVerification = {
              ...userVerification,
              redirectToEmailVerification: false,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/reset-initial-password');
          } else if (accountVerificationStatus === 'approved') {
            navigate('../ev-admin/dashboard');
          } else {
            navigate('../ev-admin/profile');
          }
        })
        .catch((error) => {
          // Display a user-friendly message
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          if (error?.response) {
            setOtpError(true);
            setOtpErrorMessage(t('invalidOtp'));
          }
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              message: error?.response
                ? error?.response?.data?.message || t('somethingWentWrong')
                : t('noInternetConnection'),
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Function to handle Resend OTP timer
   */
  const startResendTimer = () => {
    // Clear any previous interval before starting a new one
    if (intervalReference.current) clearInterval(intervalReference.current);

    setResendTimer(30); // Reset to 60 seconds
    intervalReference.current = setInterval(() => {
      setResendTimer((previous) => {
        if (previous <= 1) {
          if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval at the end
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to resend OTP
   */
  const handleResendOTP = () => {
    if (resendTimer === 0) {
      startResendTimer();
      setOTP(Array(6).fill(''));
      setFormStatus({
        showFormErrors: false,
        buttonLoader: false,
      });
      setOtpError(false);
      setOtpErrorMessage('');
      auth
        .resendEmailOTP()
        .then(() => {
          dispatch(
            setNotificationDetails({
              type: 'success',
              notificationDetails: {
                statusCode: 200,
                message: t('resendOtpSentSuccessfully'),
              },
            }),
          );
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response
                  ? error?.response?.data?.message || t('somethingWentWrong')
                  : t('noInternetConnection'),
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    startResendTimer(); // Start the timer when the component mounts
    return () => {
      if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval on unmount
    };
  }, []);

  return (
    <div className="verification-v2-wrap" dir={i18n.dir()}>
      <h2 className="title">{t('enterTheOtpEmail')}</h2>
      <div className="otp-v2-container">
        <CustomOtpInput
          length={6}
          OTP={OTP}
          setOTP={setOTP}
          type="number"
          inputLabel={t('enterOtp')}
          error={otpError || (formStatus.showFormErrors && OTP.some((digit) => digit === ''))}
          errorMessage={resolveOtpErrorMessage()}
        />
        <div className="resend-otp-v2-wrap">
          <div className="resend-timer-v2-wrap">
            <span className="resend-icon">
              <TimerIcon />
            </span>
            <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
          </div>
          <p
            role="presentation"
            onClick={handleResendOTP}
            className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
            {t('resend')}
          </p>
        </div>
        <div className="submit-button-v2-container">
          <MyButton
            label={t('proceed')}
            buttonType="submit"
            onClickFunc={handleSubmitOtp}
            loading={formStatus.buttonLoader}
            disableButton={formStatus.buttonLoader}
          />
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
